import React, { useState, useEffect, useMemo } from 'react';
import { Platform, View, Text, TextInput, StyleSheet, TouchableOpacity } from 'react-native';
import FontFamily from '../../../../constants/fonts';
import { useTheme } from '../../../../theme/useTheme';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../store';
import {
  Banner,
  pruneExpiredBanners,
  createHomepageBanner, updateHomepageBanner, deleteHomepageBanner,
} from '../../../../store/slices/adminSlice';
import { useToast } from '../../../../components/LhToastContainer';
import LHDataTable from '../../../../components/LHDataTable';
import { AdminColors } from '../../constants/adminColors';
import AdminTag from '../../components/AdminTag';
import AdminButton from '../../components/AdminButton';
import AdminModal from '../../components/AdminModal';
import { AdminFormField } from '../../components/AdminFormField';
import LhDatePicker from '../../../../components/LhDatePicker';
import RowActions from './RowActions';
import DeleteConfirmModal from './DeleteConfirmModal';
import EmptyState from './EmptyState';
import { styles } from './styles';
import { formatDate, hasBannerOverlap } from './helpers';

export interface BannersTabProps {
  showAdd: boolean;
  onCloseAdd: () => void;
}

const BannersTab: React.FC<BannersTabProps> = ({ showAdd, onCloseAdd }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';
  const isAnyDark = resolvedTheme === 'dark';
  const datePickerColorScheme = resolvedTheme === 'dark' ? 'dark' : 'light';

  const dispatch = useDispatch<AppDispatch>();
  const toast = useToast();
  const { banners, bannersLoading, bannersError } = useSelector((state: RootState) => state.configuration.homepageConfig);

  const [editItem, setEditItem] = React.useState<Banner | null>(null);
  const [message, setMessage] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [messageError, setMessageError] = useState<string | null>(null);
  const [startDateError, setStartDateError] = useState<string | null>(null);
  const [endDateError, setEndDateError] = useState<string | null>(null);
  const [overlapError, setOverlapError] = useState<string | null>(null);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState<Banner | null>(null);

  // Prune banners that expired more than 24 hours ago on mount
  useEffect(() => {
    dispatch(pruneExpiredBanners());
  }, [dispatch]);

  useEffect(() => {
    if (editItem) {
      setMessage(editItem.message);
      setStartDate(editItem.startDate ? new Date(editItem.startDate) : null);
      setEndDate(editItem.endDate ? new Date(editItem.endDate) : null);
    } else if (!showAdd) {
      setMessage('');
      setStartDate(null);
      setEndDate(null);
    }
    setMessageError(null);
    setStartDateError(null);
    setEndDateError(null);
    setOverlapError(null);
  }, [editItem, showAdd]);

  const isDirty = editItem
    ? (
      message !== editItem.message ||
      formatDate(startDate) !== editItem.startDate ||
      formatDate(endDate) !== editItem.endDate
    )
    : (!!message || !!startDate || !!endDate);

  const doClose = () => {
    onCloseAdd();
    setEditItem(null);
    setMessageError(null);
    setStartDateError(null);
    setEndDateError(null);
    setOverlapError(null);
    setShowUnsavedWarning(false);
  };

  const handleClose = () => {
    if (isDirty) { setShowUnsavedWarning(true); return; }
    doClose();
  };

  const handleSave = async () => {
    setMessageError(null);
    setStartDateError(null);
    setEndDateError(null);
    setOverlapError(null);

    let hasError = false;
    if (!message.trim()) { setMessageError('Banner message is required.'); hasError = true; }
    if (!startDate) { setStartDateError('Start date is required.'); hasError = true; }
    if (!endDate) { setEndDateError('End date is required.'); hasError = true; }
    if (hasError) return;

    const start = formatDate(startDate);
    const end = formatDate(endDate);

    // Date order validation
    if (start && end && end < start) {
      setEndDateError('End Date must be on or after Start Date.');
      return;
    }

    // Overlap validation
    if (hasBannerOverlap(start, end, banners, editItem?.id)) {
      setOverlapError('Banner dates conflict with an existing banner. Only one banner can be active at a time.');
      return;
    }
    if (editItem) {
      try {
        setIsSaving(true);
        console.log('📤 Dispatching updateHomepageBanner with:', { id: editItem.id, message: message.trim(), startDate: start, endDate: end });
        const result = await dispatch(updateHomepageBanner({ id: editItem.id, message: message.trim(), startDate: start, endDate: end })).unwrap();
        console.log('📥 updateHomepageBanner result:', result);
        toast.showToast({ header: 'Success', subtext: 'Banner updated successfully.', variant: 'success' });
        doClose();
      } catch (error: any) {
        setOverlapError(error || 'Failed to update banner. Please try again.');
      } finally {
        setIsSaving(false);
      }
    } else {
      try {
        setIsSaving(true);
        await dispatch(createHomepageBanner({ message: message.trim(), startDate: start, endDate: end })).unwrap();
        toast.showToast({ header: 'Success', subtext: 'Banner created successfully.', variant: 'success' });
        doClose();
      } catch (error: any) {
        setOverlapError(error || 'Failed to create banner. Please try again.');
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    try {
      setIsDeleting(true);
      await dispatch(deleteHomepageBanner({ id: target.id })).unwrap();
      toast.showToast({ header: 'Success', subtext: 'Banner deleted successfully.', variant: 'success' });
      setDeleteTarget(null);
    } catch (error: any) {
      toast.showToast({ header: 'Error', subtext: error || 'Failed to delete banner. Please try again.', variant: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const modalVisible = showAdd || editItem !== null;

  // Sort banners by startDate ascending (earlier dates first)
  const sortedBanners = useMemo(
    () => [...banners].sort((a, b) => a.startDate.localeCompare(b.startDate)),
    [banners],
  );

  const bannerColumns = [
    {
      columnKey: 'message',
      header: 'Message',
      flexGrow: 3,
      renderCell: (item: Banner) => (
        <Text style={{ color: isDark ? colors.text.primary : '#3A3A3A', fontSize: 14 }} numberOfLines={2} ellipsizeMode="tail">{item.message}</Text>
      ),
    },
    { columnKey: 'startDate', header: 'Start Date (CST)', flexGrow: 1.4 },
    {
      columnKey: 'endDate',
      header: 'End Date (CST)',
      flexGrow: 1.4,
      renderCell: (item: Banner) => (
        <Text style={item.status === 'Expired' ? { color: '#DC2626', fontWeight: '600' as const } : isDark ? { color: colors.text.primary } : {}}>
          {item.endDate}
        </Text>
      ),
    },
    {
      columnKey: 'status',
      header: 'Status',
      flexGrow: 1,
      renderCell: (item: Banner) => (
        <AdminTag
          label={item.status}
          variant={item.status === 'Active' ? 'green' : item.status === 'Scheduled' ? 'blue' : 'red'}
          small
        />
      ),
    },
    {
      columnKey: '_actions',
      header: 'Actions',
      flexGrow: 1,
      sortable: false,
      renderCell: (item: Banner) => (
        <RowActions onEdit={() => setEditItem(item)} onDelete={() => setDeleteTarget(item)} />
      ),
    },
  ];

  return (
    <View>
      {bannersLoading && sortedBanners.length === 0 ? (
        <EmptyState title="Loading Banners" subtitle="Please wait..." />
      ) : bannersError && sortedBanners.length === 0 ? (
        <EmptyState title="Unable to Load Banners" subtitle={bannersError} />
      ) : sortedBanners.length === 0 ? (
        <EmptyState />
      ) : (
        <LHDataTable
          data={sortedBanners}
          columns={bannerColumns}
          tableHeader=""
          tableDescription={`Showing ${sortedBanners.length} result(s)`}
          // maxRecordsToShow={sortedBanners.length}
          onRowClick={() => { /* Disable row click overlay */ }}
          disableRowHighlight
        />
      )}

      {/* Create / Edit Modal */}
      <AdminModal
        visible={modalVisible}
        title={editItem ? 'Edit Banner' : 'Add New Banner'}
        onClose={handleClose}
        footer={
          <>
            <AdminButton label="Cancel" variant="secondary" onPress={doClose} disabled={isSaving} />
            <AdminButton label={editItem ? 'Save Changes' : 'Save'} variant="primary" onPress={handleSave} loading={isSaving} />
          </>
        }
      >
        {showUnsavedWarning && (
          <View
            style={[
              bannerLocalStyles.warningBanner,
              isAnyDark && {
                backgroundColor: colors.statusBackground.warning,
                borderColor: colors.status.warning,
              },
            ]}
          >
            <View style={bannerLocalStyles.warningBannerContent}>
              <Text style={[bannerLocalStyles.warningIcon, isAnyDark && { color: colors.status.warning }]}>⚠</Text>
              <Text style={[bannerLocalStyles.warningMessage, isAnyDark && { color: colors.status.warning }]}>
                You have unsaved changes. To discard and close, click{' '}
                <Text style={bannerLocalStyles.warningMessageBold}>Cancel</Text>.
              </Text>
            </View>
            <TouchableOpacity onPress={() => setShowUnsavedWarning(false)} style={bannerLocalStyles.warningCloseBtn}>
              <Text style={[bannerLocalStyles.warningCloseText, isAnyDark && { color: colors.status.warning }]}>✕</Text>
            </TouchableOpacity>
          </View>
        )}
        <AdminFormField label="Banner Message" required hint="Max 200 characters" error={messageError ?? undefined}>
          <TextInput
            style={[
              styles.textArea,
              isAnyDark && {
                backgroundColor: colors.input.background,
                borderColor: colors.input.border,
                color: colors.text.primary,
              },
            ]}
            placeholder="Enter your announcement..."
            maxLength={200}
            multiline
            numberOfLines={3}
            value={message}
            onChangeText={(v) => { setMessage(v); setMessageError(null); }}
            placeholderTextColor={isAnyDark ? colors.text.disabled : AdminColors.gray350}
          />
          <Text style={[styles.charCount, isAnyDark && { color: colors.text.disabled }]}>{message.length} / 200</Text>
        </AdminFormField>
        <View style={styles.formRow}>
          <View style={styles.formHalf}>
            <AdminFormField label="Start Date" required hint="All dates in CST" error={startDateError ?? undefined}>
              <LhDatePicker
                value={startDate}
                onChange={(date) => { setStartDate(date); setStartDateError(null); setOverlapError(null); }}
                placeholder="Select Start Date"
                colorScheme={isAnyDark ? 'dark' : 'light'}
                iconColor={isAnyDark ? colors.text.primary : '#555'}
                disabledIconColor={isAnyDark ? colors.text.disabled : '#bdbdbd'}
              />
            </AdminFormField>
          </View>
          <View style={styles.formHalf}>
            <AdminFormField label="End Date" required hint="All dates in CST" error={endDateError ?? undefined}>
              <LhDatePicker
                value={endDate}
                onChange={(date) => { setEndDate(date); setEndDateError(null); setOverlapError(null); }}
                placeholder="Select End Date"
                minimumDate={startDate ?? undefined}
                colorScheme={isAnyDark ? 'dark' : 'light'}
                iconColor={isAnyDark ? colors.text.primary : '#555'}
                disabledIconColor={isAnyDark ? colors.text.disabled : '#bdbdbd'}
              />
            </AdminFormField>
          </View>
        </View>
        {overlapError ? <Text style={styles.fieldError}>{overlapError}</Text> : null}
      </AdminModal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        visible={deleteTarget !== null}
        itemLabel={deleteTarget?.message ?? ''}
        message="Are you sure you want to delete this banner?"
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        loading={isDeleting}
      />
    </View>
  );
};

export default BannersTab;

const bannerLocalStyles = StyleSheet.create({
  warningBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    backgroundColor: '#FFF3E0',
    borderWidth: 1,
    borderColor: '#FFB74D',
    borderRadius: 6,
    marginBottom: 12,
    padding: 12,
  },
  warningBannerContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  warningIcon: {
    fontSize: 15,
    color: '#E65100',
  },
  warningMessage: {
    flex: 1,
    fontSize: 13,
    color: '#E65100',
    lineHeight: 18,
    fontFamily: FontFamily.regular,
  },
  warningMessageBold: {
    fontWeight: '700',
    fontFamily: FontFamily.medium,
  },
  warningCloseBtn: {
    padding: 2,
    marginLeft: 8,
  },
  warningCloseText: {
    fontSize: 14,
    color: '#E65100',
    fontWeight: '700',
  },
});

// Dark mode warning banner colors (applied inline via isDark)
// bg: colors.statusBackground.warning  border: colors.status.warning  text: colors.status.warning

